/* eslint-disable @typescript-eslint/no-explicit-any */

// Imports for external libraries go here.
import React, { FC, useEffect, useRef, useState } from 'react';
import { MapInfoWindow, MapMarker, MapMarkerCluster } from '@marriott/mi-ui-library';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { MapClustersComponentProps } from './MapClustersComponent.types';
import { StyledMapClustersComponent } from './MapClustersComponent.styles';
import { HotelCard } from '../HotelCard';
import { ClusterSvgIcon, MapPinActiveSvgIcon, MapPinSvgIcon } from '../../modules/constants/constant';
import { dataUrl, fetchHDData } from '../../modules/helpers';
import { MapViewSlider } from '../MapViewSlider';
import { phoenixLuxuryPropertyInfo } from '../MapView/queries';

// Use named rather than default exports.
export const MapClustersComponent: FC<MapClustersComponentProps> = ({
  markers,
  openInfoWindow,
  setOpenInfoWindow,
  ctaLabel,
  showReviews,
  showSlider,
  setShowSlider,
  viewPort,
  requestID,
  currentLocale,
  client,
  setSelectedMarker,
  selectedMarker,
  currentSlideIndex,
  setCurrentSlideIndex,
}) => {
  /**
   * Ref's here...
   */
  const clustererRef = useRef<any>();
  const divRef = useRef(null);

  /**
   * States here...
   */
  const [containerWidth, setContainerWidth] = useState(0);
  const [infoWindowRes, setInfoWindowRes] = useState<any>(null);
  const [sliderData, setSliderData] = useState<any>(null);

  useEffect(() => {
    const handleResize = (entries: any) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    };

    const observer = new window.ResizeObserver(handleResize);
    if (divRef.current) {
      observer.observe(divRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const getWindowAroundIndex = (markers: any, index: number, windowSize = 5) => {
    const start = Math.max(0, index - windowSize);
    const end = Math.min(markers?.length, index + windowSize + 1);
    return markers?.slice(start, end);
  };

  const mapMarkerOnClick = (markerData: any, index: number) => {
    let propertyInfoVar;
    if (viewPort === 'Mobile') {
      setShowSlider(true);
      const sliderMarkers = getWindowAroundIndex(markers, index);
      const currMarkerIndex = sliderMarkers?.findIndex(
        (item: any) =>
          item?.node?.basicInformation?.latitude === markerData?.node?.basicInformation?.latitude &&
          item?.node?.basicInformation?.longitude === markerData?.node?.basicInformation?.longitude
      );
      setCurrentSlideIndex(currMarkerIndex);
      const key = 'id';
      const marshaIds = sliderMarkers?.map((obj: any) => String(obj?.node[key]));
      propertyInfoVar = {
        ids: marshaIds,
      };
    } else {
      setOpenInfoWindow(true);
      propertyInfoVar = {
        ids: markerData?.node?.id,
      };
    }
    setSelectedMarker(markerData);
    getData(phoenixLuxuryPropertyInfo, propertyInfoVar);
  };

  /**
   * Cluster's custom styles
   */
  const customClusterStyles = [
    {
      url: dataUrl(ClusterSvgIcon),
      textColor: 'white',
      width: 74,
      height: 74,
      textSize: 14,
      fontWeight: '500',
    },
  ];

  const getData = async (query: any, variables: any) => {
    const data = await fetchHDData(client, query, {
      variables: variables,
      context: {
        headers: {
          'x-request-id': requestID,
          'accept-language': currentLocale?.replace('_', '-'),
        },
      },
    });
    viewPort == 'Mobile' ? setSliderData(data) : setInfoWindowRes(data);
  };

  return (
    <StyledMapClustersComponent
      data-testid="luxury-MapClustersComponent"
      data-component-name="m-luxury-MapClustersComponent"
      ref={divRef}
    >
      <MapMarkerCluster
        averageCenter={false}
        gridSize={40}
        onLoad={clusterer => {
          clustererRef.current = clusterer;
        }}
        options={{
          imagePath: '', // Empty string to disable default cluster icon
          styles: customClusterStyles, // Use the custom cluster styles
        }}
        zoomOnClick={true}
        maxZoom={20}
      >
        {(clusterer: any) => (
          <>
            {markers?.map((marker: any, index: number) => (
              <React.Fragment key={marker.node.id || index}>
                <MapMarker
                  icon={{
                    url:
                      selectedMarker?.node?.basicInformation?.latitude === marker?.node?.basicInformation?.latitude &&
                      selectedMarker?.node?.basicInformation?.longitude === marker?.node?.basicInformation?.longitude &&
                      ((openInfoWindow && infoWindowRes) || (showSlider && sliderData))
                        ? dataUrl(MapPinActiveSvgIcon)
                        : dataUrl(MapPinSvgIcon),
                  }}
                  label={{
                    text: ' ',
                    className: 'oval',
                  }}
                  position={{
                    lat: marker?.node?.basicInformation?.latitude,
                    lng: marker?.node?.basicInformation?.longitude,
                  }}
                  onClick={() => {
                    mapMarkerOnClick(marker, index);
                  }}
                  clusterer={clusterer}
                />
              </React.Fragment>
            ))}

            {selectedMarker &&
              (infoWindowRes && openInfoWindow ? (
                <MapInfoWindow
                  options={{ ariaLabel: 'map-info-window' }}
                  position={{
                    lat: selectedMarker?.node?.basicInformation?.latitude,
                    lng: selectedMarker?.node?.basicInformation?.longitude,
                  }}
                >
                  <HotelCard
                    currentLocale={currentLocale}
                    props={infoWindowRes?.propertiesByIds?.[0]}
                    ctaLabel={ctaLabel}
                    showReviews={showReviews}
                  />
                </MapInfoWindow>
              ) : (
                showSlider &&
                sliderData && (
                  <MapViewSlider
                    searchResults={sliderData}
                    markersData={markers}
                    activeMarker={selectedMarker}
                    setSelectedMarker={setSelectedMarker}
                    containerWidth={containerWidth}
                    ctaLabel={ctaLabel}
                    isMobileView={viewPort === 'Mobile' ? true : false}
                    showReviews={showReviews}
                    currentSlideIndex={currentSlideIndex}
                    setCurrentSlideIndex={setCurrentSlideIndex}
                  />
                )
              ))}
          </>
        )}
      </MapMarkerCluster>
    </StyledMapClustersComponent>
  );
};
